import { useMemo } from 'react';
import { useGetProductQuery } from './store/apiSlice';
import { Product } from '../../types';
import { useRootSelector } from '../../store';

/**
 * Custom hook that wraps useGetProductQuery and applies embed filters from URL
 *
 * This ensures all components get consistently filtered product data
 * when accessed via embed URLs with filter parameters.
 */

export interface EmbedFilters {
  allowedStyles?: string[];
  allowedColors?: string[];
  allowedFonts?: string[];
  allowedIcons?: string[];
  allowedMaterials?: string[];
}

interface MultiProductFilterConfig extends EmbedFilters {
  productTag: string;
}

interface MultiProductRootState {
  multiProduct?: {
    activeProductTag?: string | null;
    products?: MultiProductFilterConfig[];
  };
}

function hasAnyFilters(filters: EmbedFilters | undefined): boolean {
  if (!filters) return false;
  return Boolean(
    filters.allowedStyles?.length ||
      filters.allowedColors?.length ||
      filters.allowedFonts?.length ||
      filters.allowedIcons?.length ||
      filters.allowedMaterials?.length
  );
}

function getEmbedFiltersFromURL(
  search = window.location.search
): EmbedFilters | undefined {
  // Only parse filters if we're in an embed context (URL has filter params)
  const params = new URLSearchParams(search);

  const styles = params.get('styles')?.split(',').filter(Boolean);
  const colors = params.get('colors')?.split(',').filter(Boolean);
  const fonts = params.get('fonts')?.split(',').filter(Boolean);
  const icons = params.get('icons')?.split(',').filter(Boolean);
  const materials = params.get('materials')?.split(',').filter(Boolean);

  // Only return filters if at least one filter exists
  const filters: EmbedFilters = {
    allowedStyles: styles,
    allowedColors: colors,
    allowedFonts: fonts,
    allowedIcons: icons,
    allowedMaterials: materials
  };

  if (hasAnyFilters(filters)) {
    return filters;
  }

  return undefined;
}

function getEmbedFiltersFromMultiProductState(
  state: MultiProductRootState,
  productTag: string
): EmbedFilters | undefined {
  const products = state.multiProduct?.products;
  if (!products?.length) return undefined;

  const productConfig =
    products.find(product => product.productTag === productTag) ??
    products.find(
      product => product.productTag === state.multiProduct?.activeProductTag
    );

  if (!productConfig) return undefined;

  const filters: EmbedFilters = {
    allowedStyles: productConfig.allowedStyles,
    allowedColors: productConfig.allowedColors,
    allowedFonts: productConfig.allowedFonts,
    allowedIcons: productConfig.allowedIcons,
    allowedMaterials: productConfig.allowedMaterials
  };

  if (hasAnyFilters(filters)) {
    return {
      allowedStyles: filters.allowedStyles?.length
        ? filters.allowedStyles
        : undefined,
      allowedColors: filters.allowedColors?.length
        ? filters.allowedColors
        : undefined,
      allowedFonts: filters.allowedFonts?.length
        ? filters.allowedFonts
        : undefined,
      allowedIcons: filters.allowedIcons?.length
        ? filters.allowedIcons
        : undefined,
      allowedMaterials: filters.allowedMaterials?.length
        ? filters.allowedMaterials
        : undefined
    };
  }

  return undefined;
}

function applyFiltersToProduct(
  product: Product | undefined,
  filters: EmbedFilters | undefined
): Product | undefined {
  if (!product || !filters) return product;

  const filtered = { ...product };
  const allowedIdsByGroupType: Record<string, Set<string> | undefined> = {
    styles: filters.allowedStyles?.length
      ? new Set(filters.allowedStyles)
      : undefined,
    palettes: filters.allowedColors?.length
      ? new Set(filters.allowedColors)
      : undefined,
    fonts: filters.allowedFonts?.length
      ? new Set(filters.allowedFonts)
      : undefined,
    icons: filters.allowedIcons?.length
      ? new Set(filters.allowedIcons)
      : undefined,
    materials: filters.allowedMaterials?.length
      ? new Set(filters.allowedMaterials)
      : undefined
  };

  // Filter styles
  if (filters.allowedStyles && filters.allowedStyles.length > 0) {
    filtered.styles = product.styles?.filter(style =>
      filters.allowedStyles!.includes(style.style_id)
    );
  }

  // Filter palettes (colors)
  if (filters.allowedColors && filters.allowedColors.length > 0) {
    filtered.palettes = product.palettes?.filter(palette =>
      filters.allowedColors!.includes(palette.palette_id)
    );
  }

  // Filter fonts
  if (filters.allowedFonts && filters.allowedFonts.length > 0) {
    filtered.fonts = product.fonts?.filter(font =>
      filters.allowedFonts!.includes(font.font_id)
    );
  }

  // Filter icons
  if (filters.allowedIcons && filters.allowedIcons.length > 0) {
    filtered.icons = product.icons?.filter(icon =>
      filters.allowedIcons!.includes(icon.icon_id)
    );
  }

  // Filter materials
  if (filters.allowedMaterials && filters.allowedMaterials.length > 0) {
    filtered.materials = product.materials?.filter(material =>
      filters.allowedMaterials!.includes(material.material_id)
    );
  }

  filtered.filters = product.filters
    ?.map(group => {
      const allowedIds = allowedIdsByGroupType[group.type];
      if (!allowedIds) return group;

      const entity_ids = group.entity_ids.filter(entityId =>
        allowedIds.has(entityId)
      );
      return { ...group, entity_ids };
    })
    .filter(group => group.entity_ids.length > 0);

  return filtered;
}

/**
 * Hook that automatically applies embed filters from URL parameters
 * Use this instead of useGetProductQuery directly in components that
 * need to respect embed filtering
 */
export function useFilteredProductQuery(productTag: string) {
  const multiProductState = useRootSelector(state => {
    const rootState = state as typeof state & MultiProductRootState;
    return rootState.multiProduct;
  });
  const queryResult = useGetProductQuery(productTag);
  const embedFilters = useMemo(() => {
    const stateFilters = getEmbedFiltersFromMultiProductState(
      { multiProduct: multiProductState },
      productTag
    );
    return stateFilters ?? getEmbedFiltersFromURL();
  }, [multiProductState, productTag]);

  const filteredData = useMemo(
    () => applyFiltersToProduct(queryResult.data, embedFilters),
    [queryResult.data, embedFilters]
  );

  return {
    ...queryResult,
    data: filteredData
  };
}
